<template>
  <div class="point-gauge-parts">
    <div class="point-gauge-parts__gauge">
      <div class="point-gauge-parts__gauge-inner">
        <div
          class="point-gauge-parts__gauge-active"
          :class="`point-gauge-parts__gauge-active--${gaugeColorName}`"
          :style="`width: ${gaugeWidth};`"
        />
      </div>
    </div>

    <div class="point-gauge-parts__labels">
      <div
        v-for="(rankNamePoint, index) in rankNamePointList"
        :key="`point-gauge-parts__label${index}`"
        v-show="rankNamePoint.point > 0"
        class="point-gauge-parts__label"
        :class="[
          { 'point-gauge-parts__label--active': rankNamePoint.point <= membersRankData.point },
          `point-gauge-parts__label--${rankNamePoint.rank}`,
        ]"
      >
        <span
          v-text="displayGaugePoint(rankNamePoint.point, index === rankNamePointList.length - 1)"
        />
      </div>
    </div>

    <!-- ブロンズランクには特典がない -->
    <p
      class="point-gauge-parts__benefits"
      v-if="isFreePlan && membersRankData.currentRank !== 'bronze'"
      v-html="rankBenefitsLink"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import { MembersRankDataType } from '@/components/MypageTopPage/MembershipCardPane.vue'
import useMembershipCard from '@/components/MypageTopPage/hook/useMembershipCard'
import Const from '@/util/Const'
import I18n from '@/locales/I18n'

/**
 * マイページ: 会員証 ポイントゲージパーツ
 */
export default defineComponent({
  name: 'PointGaugeParts',
  props: {
    membersRankData: {
      type: Object as PropType<MembersRankDataType>,
      default: () => ({}),
      require: true,
    },
    isFreePlan: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const { getRankAchievementPoint } = useMembershipCard()

    /** 会員ランクリストと必要なポイント */
    const rankNamePointList = Const.MEMBERS_RANK_NAME_POINTS

    /** 会員ランク特典説明へのリンク */
    const rankBenefitsLink = computed(() =>
      I18n.t(
        `MypagePage.MypageTopPage.membersRank.nextRank.free.benefits.${props.membersRankData.currentRank}`,
        {
          url:
            I18n.locale === 'ja'
              ? Const.EXTERNAL_LINKS.ABOUT_MEMBER_RANK.JA
              : Const.EXTERNAL_LINKS.ABOUT_MEMBER_RANK.EN,
        },
      ).toString(),
    )

    /**
     * ゲージ横幅 計算
     */
    const gaugeWidth = computed(() => {
      let width = 0
      const currentRank = props.membersRankData?.currentRank
      const point = props.membersRankData?.point || 0
      const rankLength = rankNamePointList.length
      const gaugeStep = rankLength - 1
      const currentRankIndex =
        rankNamePointList.findIndex((rankPoint) => rankPoint.rank === currentRank) || 0
      const nextRank = rankNamePointList[currentRankIndex + 1]
        ? rankNamePointList[currentRankIndex + 1].rank
        : undefined
      const currentRankThreshold = getRankAchievementPoint(currentRank)
      /**
       * 最大ランクの場合、次のランクが存在しないため0を返す
       * 下記、if (gaugeStep <= currentRankIndex)のロジックに入るため、nextRankThresholdに0が入っていても問題ない
       */
      const nextRankThreshold = nextRank ? getRankAchievementPoint(nextRank) : 0

      if (gaugeStep <= currentRankIndex) {
        // 最大ランク時
        width = (point / currentRankThreshold) * 100
      } else if (currentRankIndex === 0) {
        // 最低ランク時
        width = ((point / nextRankThreshold) * 100) / gaugeStep
      } else {
        // それ以外
        width = (100 / gaugeStep) * currentRankIndex
        width +=
          (((point - currentRankThreshold) / (nextRankThreshold - currentRankThreshold)) * 100) / 3
      }

      return `${width}%`
    })

    /**
     * ゲージカラー名取得
     */
    const gaugeColorName = computed(() => {
      if (props.isFreePlan) {
        return 'free'
      }
      return props.membersRankData.currentRank
    })

    /**
     * ゲージに表示するポイント
     * TODO: GOLDより上のランクについてはリリースしないため、ひとまず●だけ表示しておく
     */
    const displayGaugePoint = (point: number, isLast: boolean) =>
      !isLast ? point.toLocaleString() : ''

    return {
      rankNamePointList,
      gaugeWidth,
      gaugeColorName,
      displayGaugePoint,
      rankBenefitsLink,
    }
  },
})
</script>

<style lang="scss" scoped>
.point-gauge-parts {
  position: relative;
  width: 100%;

  &__gauge {
    width: 100%;
    height: 8px;
    background-color: $black50;
    border-radius: 10px;
    overflow: hidden;

    &-inner {
      width: 100%;
      height: 8px;
      padding-right: 21px;
    }

    &-active {
      top: 0;
      left: 0;
      height: 8px;
      background: linear-gradient(271deg, #b46a34 0%, #de9e70 77.72%);

      &--bronze {
        background: linear-gradient(271deg, #b46a34 0%, #de9e70 77.72%);
      }

      &--silver {
        background: linear-gradient(271deg, #999 0%, #cacaca 77.72%);
      }

      &--gold {
        background: linear-gradient(271deg, #e19a0d 0%, #be830e 17%, #f6bb49 77.72%);
      }

      &--platinum {
        background: linear-gradient(271deg, #4b698c 0%, #abc9da 77.72%);
      }
    }
  }

  &__labels {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: calc(100% - 21px);
  }

  &__label {
    position: absolute;
    top: 0;
    transform: translate(50%, 0);
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 1px;
    flex-direction: column;
    gap: 5px;

    &::before {
      display: block;
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.13);
    }

    &--active::before {
      background: $white60;
    }

    span {
      display: block;
      color: $white70;
      text-align: center;
      font-size: 10px;
      font-weight: 600;
      line-height: 1;
      letter-spacing: 0.02em;
    }

    &--silver {
      right: 66.66%;
    }

    &--gold {
      right: 33.33%;
    }

    &--platinum {
      right: 0;
    }
  }

  &__benefits {
    margin-top: 23px;
    margin-bottom: -14px;
    width: 100%;
    text-align: center;
    color: $white50;
    font-size: 10px;
    font-weight: 500;
    line-height: 1.6;
    letter-spacing: 0.02em;
  }
}
</style>

<style lang="scss">
.point-gauge-parts {
  &__benefits {
    a {
      color: $white70;
      text-decoration: underline;
    }

    @media screen and (min-width: $responsiveTabletWidth) {
      br {
        display: none;
      }
    }
  }
}
</style>
